import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, formatBytes, formatDate, uploadWithProgress } from "../api";
import { Spinner } from "../components/ui";

/** Estúdio de autoria: fontes diversas → sumário → redação → ebook em PDF → curso. */

const msgOf = (err: unknown, fallback = "Não foi possível concluir.") =>
  err instanceof ApiError ? err.message : fallback;
const usd = (n: number) => (n > 0 && n < 0.01 ? "< US$ 0,01" : `US$ ${n.toFixed(2).replace(".", ",")}`);

const SCOPES = [
  ["process", "Processo interno"],
  ["role", "Cargo"],
  ["function", "Função"],
  ["sector", "Setor"],
  ["theme", "Tema livre"],
] as const;
const ORIGINS = ["Manual interno", "NotebookLM", "Gemini", "ChatGPT", "Claude", "Google Docs", "Outro"];
const RIGHTS = [
  ["internal", "Conteúdo interno da instituição"],
  ["owner", "Autoria própria"],
  ["licensed", "Licença de uso"],
  ["public_domain", "Domínio público"],
] as const;
const KIND_LABELS: Record<string, string> = {
  pdf: "PDF",
  docx: "Word",
  txt: "Texto",
  md: "Markdown",
  text: "Texto colado",
  url: "Página",
};
const STATUS_LABELS: Record<string, [string, string]> = {
  draft: ["Fontes", ""],
  outlined: ["Sumário pronto", "brand"],
  writing: ["Em redação", "warn"],
  ready: ["Ebook gerado", "ok"],
};
const CH_STATUS: Record<string, [string, string]> = {
  pending: ["Aguardando redação", ""],
  draft: ["Rascunho", "warn"],
  approved: ["Aprovado", "ok"],
};

type Block = { heading?: string; text: string; sources: string[] };
type Conflict = {
  id: string;
  topic: string;
  description: string;
  positions: Array<{ source: string; claim: string }>;
  resolution?: string;
  resolvedAt?: string;
};
type OutlineChapter = {
  id?: string;
  title: string;
  purpose: string;
  sections: string[];
  sourceRefs: string[];
  gaps: string[];
};
type Source = {
  id: string;
  ref: string;
  kind: string;
  origin: string | null;
  title: string;
  originalName: string | null;
  url: string | null;
  status: "processing" | "ready" | "failed";
  error: string | null;
  charCount: number;
  pageCount: number | null;
  summary: string | null;
  topics: string[];
  priority: "normal" | "high" | "discard";
  excerpt: string;
};
type Chapter = {
  id: string;
  outlineId: string;
  position: number;
  title: string;
  status: "pending" | "draft" | "approved";
  blocks: Block[];
  conflicts: Conflict[];
  history: Array<{ at: string; reason: string }>;
  openConflicts: number;
  words: number;
};
type Project = {
  project: {
    id: string;
    title: string;
    subtitle: string | null;
    goal: string;
    audience: string | null;
    scopeKind: string;
    scopeName: string;
    scopeLabel: string | null;
    tone: string | null;
    targetPages: number;
    authorName: string | null;
    status: string;
    outline: { chapters: Required<OutlineChapter>[]; gaps: string[]; generatedAt?: string } | null;
    exports: Array<{ version: number; pages: number; sizeBytes: number; createdAt: string }>;
    courseId: string | null;
    updatedAt: string;
  };
  sources: Source[];
  chapters: Chapter[];
  writing: null | {
    status: string;
    progress?: { step?: string; done?: number; total?: number } | null;
    error: string | null;
  };
  processing: number;
};
type Estimate = {
  sources: number;
  sourceChars: number;
  chapters: number;
  outlineUsd: number;
  chapterUsd: number;
  writingUsd: number;
  models: { outline: string | null; writing: string | null };
};

// ─────────────────────────── Lista e novo projeto ───────────────────────────

type ProjectRow = {
  id: string;
  title: string;
  goal: string;
  scope: string;
  status: string;
  sources: number;
  chapters: number;
  exports: number;
  courseId: string | null;
  updatedAt: string;
};

export function StudioListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ProjectRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<ProjectRow[]>("/authoring")
      .then((rows) => {
        setItems(rows);
        if (!rows.length) setOpen(true);
      })
      .catch((err) => setError(msgOf(err)));
  }, []);

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    setBusy(true);
    setError("");
    setFields({});
    try {
      const res = await api.post<{ id: string }>("/authoring", {
        ...form,
        targetPages: Number(form.targetPages) || 30,
      });
      navigate(`/professor/estudio/${res.id}`);
    } catch (err) {
      setError(msgOf(err));
      if (err instanceof ApiError) setFields(err.fields);
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Área do professor</div>
          <h1>Estúdio de autoria</h1>
          <p>
            Reúna manuais, anotações, cadernos de IA e páginas da internet em um ebook autoral, revisado por
            você, pronto para virar curso.
          </p>
        </div>
        {!open && (
          <button className="btn" onClick={() => setOpen(true)}>
            Novo projeto
          </button>
        )}
      </div>
      {error && !open && <p className="alert error">{error}</p>}
      {open && (
        <form className="card card-pad form studio-new" onSubmit={create} noValidate>
          <h2>Novo projeto de autoria</h2>
          {error && <p className="alert error">{error}</p>}
          <div className="grid-2">
            <TextField name="title" label="Título do ebook" error={fields.title} required maxLength={120} />
            <TextField name="subtitle" label="Subtítulo (opcional)" maxLength={120} />
          </div>
          <div className="field">
            <label htmlFor="goal">Objetivo</label>
            <textarea
              id="goal"
              name="goal"
              rows={2}
              maxLength={600}
              placeholder="Ex.: padronizar o atendimento de trocas e devoluções nas lojas da rede."
              aria-invalid={fields.goal ? true : undefined}
            />
            {fields.goal && <span className="error">{fields.goal}</span>}
          </div>
          <div className="grid-2">
            <TextField name="audience" label="Público" placeholder="Ex.: atendentes e líderes de loja" />
            <TextField name="tone" label="Tom" placeholder="Ex.: direto e acolhedor" />
          </div>
          <div className="grid-3">
            <div className="field">
              <label htmlFor="scopeKind">Recorte</label>
              <select id="scopeKind" name="scopeKind" defaultValue="process">
                {SCOPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <TextField name="scopeLabel" label="Qual?" placeholder="Ex.: Trocas e devoluções" />
            <TextField
              name="targetPages"
              label="Extensão (páginas)"
              type="number"
              min={8}
              max={200}
              defaultValue="30"
            />
          </div>
          <TextField
            name="authorName"
            label="Autoria exibida na capa (opcional)"
            placeholder="Seu nome ou o da equipe"
          />
          <div className="row-between">
            {items?.length ? (
              <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
                Voltar à lista
              </button>
            ) : (
              <span />
            )}
            <button className="btn" disabled={busy}>
              {busy ? "Criando…" : "Criar projeto e incluir fontes"}
            </button>
          </div>
        </form>
      )}
      {!items ? (
        <Spinner />
      ) : (
        !open &&
        items.length > 0 && (
          <div className="ebook-list">
            {items.map((p) => {
              const [label, tone] = STATUS_LABELS[p.status] ?? [p.status, ""];
              return (
                <Link key={p.id} to={`/professor/estudio/${p.id}`} className="card ebook-row">
                  <span className="book-icon studio-icon">✎</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="title">{p.title}</span>
                    <span className="meta">
                      {p.scope} · {p.sources} fonte(s) · {p.chapters} capítulo(s)
                      {p.exports ? ` · ${p.exports} versão(ões) em PDF` : ""} · atualizado em{" "}
                      {formatDate(p.updatedAt)}
                    </span>
                  </span>
                  <span className={`badge ${tone}`}>
                    <span className="dot" /> {label}
                  </span>
                </Link>
              );
            })}
          </div>
        )
      )}
    </main>
  );
}

function TextField({
  label,
  error,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const id = `f-${props.name}`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} aria-invalid={error ? true : undefined} {...props} />
      {error && <span className="error">{error}</span>}
    </div>
  );
}

// ─────────────────────────── Projeto ───────────────────────────

const TABS = [
  ["fontes", "1 · Fontes"],
  ["sumario", "2 · Sumário"],
  ["redacao", "3 · Redação"],
  ["ebook", "4 · Ebook e curso"],
] as const;

export function StudioProjectPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("etapa") ?? "fontes") as (typeof TABS)[number][0];
  const [data, setData] = useState<Project | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      api
        .get<Project>(`/authoring/${id}`)
        .then(setData)
        .catch((err) => setError(msgOf(err))),
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const busy = Boolean(
    data && (data.processing > 0 || (data.writing && ["queued", "running"].includes(data.writing.status))),
  );
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void load(), 2500);
    return () => clearInterval(t);
  }, [busy, load]);

  if (error && !data)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
      </main>
    );
  if (!data)
    return (
      <main className="page">
        <Spinner />
      </main>
    );
  const p = data.project;
  const [label, tone] = STATUS_LABELS[p.status] ?? [p.status, ""];
  const ready = data.sources.filter((s) => s.status === "ready" && s.priority !== "discard").length;
  const written = data.chapters.filter((c) => c.blocks.length).length;
  const steps: Record<string, string> = {
    fontes: `${ready} pronta(s)`,
    sumario: p.outline ? `${p.outline.chapters.length} capítulo(s)` : "a propor",
    redacao: data.chapters.length ? `${written}/${data.chapters.length}` : "—",
    ebook: p.exports.length ? `v${p.exports.at(-1)!.version}` : "—",
  };
  return (
    <main className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to="/professor/estudio">Estúdio de autoria</Link> / Projeto
          </div>
          <h1>{p.title}</h1>
          <p>
            {p.scopeName}
            {p.scopeLabel ? ` · ${p.scopeLabel}` : ""} · cerca de {p.targetPages} páginas · {p.goal}
          </p>
        </div>
        <span className={`badge ${tone}`}>
          <span className="dot" /> {label}
        </span>
      </div>
      <div className="tabs studio-steps" role="tablist">
        {TABS.map(([key, l]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setParams({ etapa: key }, { replace: true })}
          >
            {l} <small>{steps[key]}</small>
          </button>
        ))}
      </div>
      {tab === "fontes" && (
        <SourcesTab data={data} reload={load} next={() => setParams({ etapa: "sumario" })} />
      )}
      {tab === "sumario" && (
        <OutlineTab data={data} reload={load} next={() => setParams({ etapa: "redacao" })} />
      )}
      {tab === "redacao" && (
        <WritingTab data={data} reload={load} next={() => setParams({ etapa: "ebook" })} />
      )}
      {tab === "ebook" && <EbookTab data={data} reload={load} />}
    </main>
  );
}

type TabProps = { data: Project; reload: () => Promise<void>; next?: () => void };

// ─────────────────────────── 1. Fontes ───────────────────────────

function SourcesTab({ data, reload, next }: TabProps) {
  const id = data.project.id;
  const [mode, setMode] = useState<"file" | "text" | "url">("file");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!fd.get("rightsAccepted")) return setError("Confirme o direito de uso da fonte.");
    fd.set("rightsAccepted", "true");
    setError("");
    try {
      if (mode === "file") {
        if (!file) return setError("Escolha um arquivo PDF, DOCX, TXT ou Markdown.");
        fd.set("file", file);
        setProgress(0);
        await uploadWithProgress(`/authoring/${id}/sources`, fd, (p) => setProgress(p));
      } else {
        const body = Object.fromEntries(fd) as Record<string, string>;
        await api.post(`/authoring/${id}/sources`, { ...body, kind: mode, rightsAccepted: true });
      }
      formRef.current?.reset();
      setFile(null);
      await reload();
    } catch (err) {
      setError(msgOf(err));
    } finally {
      setProgress(null);
    }
  }
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(msgOf(err));
    }
  };
  async function toggleText(s: Source) {
    if (viewing[s.id] !== undefined) return setViewing(({ [s.id]: _, ...rest }) => rest);
    const full = await api.get<{ text: string }>(`/authoring/${id}/sources/${s.id}`);
    setViewing((v) => ({ ...v, [s.id]: full.text }));
  }

  const ready = data.sources.filter((s) => s.status === "ready" && s.priority !== "discard").length;
  return (
    <div className="studio-grid">
      <form ref={formRef} className="card card-pad form" onSubmit={add} noValidate>
        <h2 className="studio-h">Incluir fonte</h2>
        <div className="seg" role="group" aria-label="Tipo de fonte">
          {(
            [
              ["file", "Arquivo"],
              ["text", "Texto colado"],
              ["url", "Página da internet"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={`btn small ${mode === k ? "" : "ghost"}`}
              onClick={() => setMode(k)}
            >
              {l}
            </button>
          ))}
        </div>
        {mode === "file" && (
          <div className="field">
            <label htmlFor="src-file">PDF, DOCX, TXT ou Markdown (até 30 MB)</label>
            <input
              id="src-file"
              type="file"
              accept=".pdf,.docx,.txt,.md,.markdown,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="hint">
              Cadernos do NotebookLM ou do Gemini e documentos do Google entram pela exportação em PDF, DOCX
              ou texto.
            </span>
          </div>
        )}
        {mode === "text" && (
          <div className="field">
            <label htmlFor="src-text">Texto</label>
            <textarea
              id="src-text"
              name="text"
              rows={7}
              placeholder="Cole aqui anotações, respostas de um caderno de IA ou trechos de documentos."
            />
          </div>
        )}
        {mode === "url" && (
          <TextField name="url" label="Endereço da página pública" type="url" placeholder="https://" />
        )}
        <div className="grid-2">
          <TextField name="title" label="Título da fonte (opcional)" maxLength={200} />
          <div className="field">
            <label htmlFor="src-origin">Origem</label>
            <select id="src-origin" name="origin" defaultValue="Manual interno">
              {ORIGINS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="src-rights">Direito de uso</label>
          <select id="src-rights" name="rightsBasis" defaultValue="internal">
            {RIGHTS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <label className="check">
          <input type="checkbox" name="rightsAccepted" value="true" /> Declaro que tenho direito de usar esta
          fonte na criação do ebook.
        </label>
        {error && <p className="alert error">{error}</p>}
        <button className="btn" disabled={progress !== null}>
          {progress !== null ? `Enviando… ${progress}%` : "Incluir fonte"}
        </button>
      </form>

      <section className="stack">
        <div className="row-between">
          <h2 className="studio-h">
            Fontes do projeto{" "}
            <small className="segment-meta">
              {data.sources.length} incluída(s) · {ready} pronta(s) para uso
            </small>
          </h2>
          {ready > 0 && next && (
            <button className="btn small" onClick={next}>
              Seguir para o sumário →
            </button>
          )}
        </div>
        {!data.sources.length && (
          <div className="card empty">
            <h3>Nenhuma fonte ainda</h3>
            <p>
              Comece pelos documentos que já existem: manuais, procedimentos, apresentações exportadas em PDF
              e notas.
            </p>
          </div>
        )}
        {data.sources.map((s) => (
          <article
            key={s.id}
            className={`card card-pad source-card${s.priority === "discard" ? " discarded" : ""}`}
          >
            <header className="row-between">
              <div className="source-title">
                <span className="ref">{s.ref}</span>
                <div>
                  <strong>{s.title}</strong>
                  <div className="segment-meta">
                    {KIND_LABELS[s.kind] ?? s.kind}
                    {s.origin ? ` · ${s.origin}` : ""}
                    {s.pageCount ? ` · ${s.pageCount} páginas` : ""}
                    {s.charCount ? ` · ${s.charCount.toLocaleString("pt-BR")} caracteres` : ""}
                    {s.url ? ` · ${s.url}` : ""}
                  </div>
                </div>
              </div>
              {s.status === "processing" ? (
                <span className="badge warn">
                  <span className="dot" /> Lendo e resumindo
                </span>
              ) : s.status === "failed" ? (
                <span className="badge error">
                  <span className="dot" /> Falhou
                </span>
              ) : (
                <select
                  aria-label={`Prioridade de ${s.ref}`}
                  value={s.priority}
                  onChange={(e) =>
                    act(() => api.patch(`/authoring/${id}/sources/${s.id}`, { priority: e.target.value }))
                  }
                >
                  <option value="normal">Usar</option>
                  <option value="high">Prioritária</option>
                  <option value="discard">Descartar</option>
                </select>
              )}
            </header>
            {s.error && <p className={`alert ${s.status === "failed" ? "error" : ""}`}>{s.error}</p>}
            {s.summary && <p className="source-summary">{s.summary}</p>}
            {s.topics.length > 0 && (
              <div className="seg">
                {s.topics.map((t) => (
                  <span key={t} className="badge">
                    {t}
                  </span>
                ))}
              </div>
            )}
            {viewing[s.id] !== undefined && <pre className="source-text">{viewing[s.id]}</pre>}
            <div className="seg">
              {s.status === "ready" && (
                <button className="btn ghost small" onClick={() => void toggleText(s)}>
                  {viewing[s.id] !== undefined ? "Ocultar texto" : "Ver texto extraído"}
                </button>
              )}
              {s.status === "failed" && (
                <button
                  className="btn ghost small"
                  onClick={() => act(() => api.post(`/authoring/${id}/sources/${s.id}/retry`))}
                >
                  Tentar de novo
                </button>
              )}
              <button
                className="btn ghost small"
                onClick={() => act(() => api.del(`/authoring/${id}/sources/${s.id}`))}
              >
                Remover
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

// ─────────────────────────── 2. Sumário ───────────────────────────

function useEstimate(id: string, deps: unknown[]) {
  const [est, setEst] = useState<Estimate | null>(null);
  useEffect(() => {
    api
      .get<Estimate>(`/authoring/${id}/estimate`)
      .then(setEst)
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ...deps]);
  return est;
}

function OutlineTab({ data, reload, next }: TabProps) {
  const p = data.project;
  const refs = data.sources.filter((s) => s.status === "ready" && s.priority !== "discard").map((s) => s.ref);
  const est = useEstimate(p.id, [data.sources.length, p.outline?.chapters.length]);
  const [draft, setDraft] = useState<OutlineChapter[]>(() => p.outline?.chapters ?? []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  // Sumário novo ou salvo no servidor: o rascunho local acompanha a versão recebida.
  const outlineKey = JSON.stringify(p.outline?.chapters ?? []);
  const [seenKey, setSeenKey] = useState(outlineKey);
  if (seenKey !== outlineKey) {
    setSeenKey(outlineKey);
    setDraft(p.outline?.chapters ?? []);
  }

  async function propose() {
    const written = data.chapters.some((c) => c.blocks.length);
    if (written && !window.confirm("Um novo sumário substitui os capítulos já redigidos. Deseja continuar?"))
      return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ costUsd: number }>(`/authoring/${p.id}/outline`, { replace: written });
      await reload();
      setMsg({
        kind: "ok",
        text: `Sumário proposto (custo: ${usd(r.costUsd)}). Ajuste o que quiser e salve.`,
      });
    } catch (err) {
      setMsg({ kind: "error", text: msgOf(err) });
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(`/authoring/${p.id}/outline`, { chapters: draft, gaps: p.outline?.gaps ?? [] });
      await reload();
      setMsg({ kind: "ok", text: "Sumário salvo. Os capítulos já estão disponíveis na etapa de redação." });
    } catch (err) {
      setMsg({ kind: "error", text: msgOf(err) });
    } finally {
      setBusy(false);
    }
  }
  const update = (i: number, patch: Partial<OutlineChapter>) =>
    setDraft((d) => d.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const move = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const n = [...d];
      const j = i + dir;
      if (j < 0 || j >= n.length) return d;
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
  const dirty = JSON.stringify(draft) !== JSON.stringify(p.outline?.chapters ?? []);

  return (
    <div className="stack">
      <div className="card card-pad gen-cta">
        <div>
          <h2 className="studio-h">Sumário com mapa de fontes</h2>
          <p className="segment-meta" style={{ margin: 0 }}>
            A IA propõe capítulos e seções a partir do objetivo e das {refs.length} fonte(s) prontas, indica
            quais fontes sustentam cada parte e aponta lacunas.
            {est &&
              ` Custo estimado: sumário ${usd(est.outlineUsd)} · redação completa ${usd(est.writingUsd)} (${est.chapters} capítulos).`}
          </p>
        </div>
        <button className="btn" onClick={() => void propose()} disabled={busy || !refs.length}>
          {busy ? "Trabalhando…" : p.outline ? "Propor novo sumário" : "Propor sumário com IA"}
        </button>
      </div>
      {msg && <p className={`alert ${msg.kind}`}>{msg.text}</p>}
      {p.outline && p.outline.gaps.length > 0 && (
        <div className="card card-pad gaps">
          <strong>Lacunas do material em relação ao objetivo</strong>
          <ul>
            {p.outline.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}
      {draft.map((c, i) => (
        <article key={c.id ?? `novo-${i}`} className="card card-pad outline-ch">
          <div className="row-between">
            <span className="ch-num">Capítulo {i + 1}</span>
            <div className="seg">
              <button
                className="btn ghost small"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Subir"
              >
                ↑
              </button>
              <button
                className="btn ghost small"
                onClick={() => move(i, 1)}
                disabled={i === draft.length - 1}
                aria-label="Descer"
              >
                ↓
              </button>
              <button
                className="btn ghost small"
                onClick={() => setDraft((d) => d.filter((_, k) => k !== i))}
              >
                Remover
              </button>
            </div>
          </div>
          <input
            className="ch-title"
            value={c.title}
            maxLength={70}
            aria-label="Título do capítulo"
            onChange={(e) => update(i, { title: e.target.value })}
          />
          <textarea
            rows={2}
            value={c.purpose}
            aria-label="Propósito"
            placeholder="Propósito do capítulo"
            onChange={(e) => update(i, { purpose: e.target.value })}
          />
          <textarea
            rows={Math.max(2, c.sections.length)}
            value={c.sections.join("\n")}
            aria-label="Seções, uma por linha"
            placeholder="Seções, uma por linha"
            onChange={(e) => update(i, { sections: e.target.value.split("\n").slice(0, 8) })}
            onBlur={() => update(i, { sections: c.sections.map((s) => s.trim()).filter(Boolean) })}
          />
          <div className="seg" aria-label="Fontes do capítulo">
            <span className="segment-meta">Fontes:</span>
            {refs.map((r) => {
              const on = c.sourceRefs.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  className={`chip-toggle${on ? " on" : ""}`}
                  aria-pressed={on}
                  title={data.sources.find((s) => s.ref === r)?.title}
                  onClick={() =>
                    update(i, { sourceRefs: on ? c.sourceRefs.filter((x) => x !== r) : [...c.sourceRefs, r] })
                  }
                >
                  {r}
                </button>
              );
            })}
          </div>
          {c.gaps.length > 0 && <p className="segment-meta gap-note">Lacunas: {c.gaps.join(" · ")}</p>}
        </article>
      ))}
      {(p.outline || draft.length > 0) && (
        <div className="row-between">
          <button
            className="btn ghost"
            onClick={() =>
              setDraft((d) => [
                ...d,
                { title: "Novo capítulo", purpose: "", sections: [], sourceRefs: refs.slice(0, 1), gaps: [] },
              ])
            }
          >
            Incluir capítulo
          </button>
          <div className="seg">
            {dirty && (
              <button className="btn" onClick={() => void save()} disabled={busy}>
                Salvar sumário
              </button>
            )}
            {!dirty && p.outline && next && (
              <button className="btn" onClick={next}>
                Seguir para a redação →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── 3. Redação ───────────────────────────

function WritingTab({ data, reload, next }: TabProps) {
  const p = data.project;
  const [sel, setSel] = useState<string | null>(data.chapters[0]?.id ?? null);
  const [error, setError] = useState("");
  const chapter = data.chapters.find((c) => c.id === sel) ?? data.chapters[0];
  const pending = data.chapters.filter((c) => c.status === "pending").length;
  const w = data.writing;
  const running = w && ["queued", "running"].includes(w.status);
  const est = useEstimate(p.id, [data.chapters.length]);

  if (!p.outline || !data.chapters.length)
    return (
      <div className="card empty">
        <h3>Sumário pendente</h3>
        <p>Proponha e salve o sumário na etapa 2 para liberar a redação dos capítulos.</p>
      </div>
    );

  async function writeAll() {
    setError("");
    try {
      await api.post(`/authoring/${p.id}/write-all`);
      await reload();
    } catch (err) {
      setError(msgOf(err));
    }
  }
  const allWritten = data.chapters.every((c) => c.blocks.length);
  return (
    <div className="stack">
      {(pending > 0 || running || w?.error) && (
        <div className="card card-pad gen-cta">
          <div>
            <h2 className="studio-h">
              {running ? "Redação em andamento" : `${pending} capítulo(s) aguardando redação`}
            </h2>
            <p className="segment-meta" style={{ margin: 0 }}>
              {running
                ? `${w?.progress?.step ?? "Na fila"}${w?.progress?.total ? ` · ${w.progress.done ?? 0}/${w.progress.total}` : ""}`
                : `A IA redige cada capítulo com as fontes indicadas no sumário, com notas de origem por trecho.${est ? ` Custo estimado por capítulo: ${usd(est.chapterUsd)}.` : ""}`}
            </p>
            {w?.error && !running && (
              <p className="alert error" style={{ marginTop: 8 }}>
                {w.error}
              </p>
            )}
          </div>
          {!running && pending > 0 && (
            <button className="btn" onClick={() => void writeAll()}>
              Redigir capítulos pendentes
            </button>
          )}
          {running && <Spinner label="Redigindo" />}
        </div>
      )}
      {error && <p className="alert error">{error}</p>}
      <div className="writing-layout">
        <nav className="card ch-list" aria-label="Capítulos">
          {data.chapters.map((c) => {
            const [l, t] = CH_STATUS[c.status]!;
            return (
              <button key={c.id} className={c.id === chapter?.id ? "on" : ""} onClick={() => setSel(c.id)}>
                <span className="ch-num">{c.position}</span>
                <span>
                  <strong>{c.title}</strong>
                  <span className="segment-meta">
                    <span className={`badge ${t}`}>{l}</span>
                    {c.words ? ` ${c.words} palavras` : ""}
                    {c.openConflicts ? ` · ${c.openConflicts} divergência(s)` : ""}
                  </span>
                </span>
              </button>
            );
          })}
          {allWritten && next && (
            <button
              className="btn small block"
              style={{ margin: 8, width: "calc(100% - 16px)" }}
              onClick={next}
            >
              Seguir para o ebook →
            </button>
          )}
        </nav>
        {chapter && (
          <ChapterEditor
            key={chapter.id}
            projectId={p.id}
            chapter={chapter}
            sources={data.sources}
            reload={reload}
          />
        )}
      </div>
    </div>
  );
}

function ChapterEditor({
  projectId,
  chapter,
  sources,
  reload,
}: {
  projectId: string;
  chapter: Chapter;
  sources: Source[];
  reload: () => Promise<void>;
}) {
  const [blocks, setBlocks] = useState<Block[]>(chapter.blocks);
  // Nova versão vinda do servidor (redação, restauração ou gravação) substitui o rascunho local.
  const serverKey = JSON.stringify(chapter.blocks);
  const [seenKey, setSeenKey] = useState(serverKey);
  if (seenKey !== serverKey) {
    setSeenKey(serverKey);
    setBlocks(chapter.blocks);
  }
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const locked = chapter.status === "approved";
  const dirty = JSON.stringify(blocks) !== JSON.stringify(chapter.blocks);
  const refs = sources.filter((s) => s.status === "ready").map((s) => s.ref);
  const base = `/authoring/${projectId}/chapters/${chapter.id}`;

  const run = async (label: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(label);
    setMsg(null);
    try {
      await fn();
      await reload();
      if (ok) setMsg({ kind: "ok", text: ok });
    } catch (err) {
      setMsg({ kind: "error", text: msgOf(err) });
    } finally {
      setBusy("");
    }
  };
  const upd = (i: number, patch: Partial<Block>) =>
    setBlocks((b) => b.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  return (
    <section className="card card-pad ch-editor">
      <div className="row-between">
        <div>
          <span className="ch-num">Capítulo {chapter.position}</span>
          <h2 className="studio-h">{chapter.title}</h2>
        </div>
        <div className="seg">
          {chapter.blocks.length > 0 &&
            (locked ? (
              <button
                className="btn ghost small"
                onClick={() => run("approve", () => api.post(`${base}/approve`, { approved: false }))}
              >
                Reabrir para edição
              </button>
            ) : (
              <button
                className="btn small"
                disabled={dirty || Boolean(busy) || chapter.openConflicts > 0}
                title={
                  chapter.openConflicts > 0
                    ? "Decida as divergências antes de aprovar"
                    : dirty
                      ? "Salve as alterações antes de aprovar"
                      : undefined
                }
                onClick={() =>
                  run("approve", () => api.post(`${base}/approve`, { approved: true }), "Capítulo aprovado.")
                }
              >
                Aprovar capítulo
              </button>
            ))}
        </div>
      </div>
      {msg && <p className={`alert ${msg.kind}`}>{msg.text}</p>}

      {chapter.conflicts.length > 0 && (
        <div className="conflicts">
          <strong>Divergências entre fontes</strong>
          {chapter.conflicts.map((c) => (
            <ConflictCard
              key={c.id}
              conflict={c}
              locked={locked}
              onSave={(resolution) =>
                run("conflict", () => api.patch(`${base}/conflicts/${c.id}`, { resolution }))
              }
            />
          ))}
        </div>
      )}

      {!chapter.blocks.length ? (
        <div className="empty">
          <p>Este capítulo ainda não tem texto.</p>
          <button
            className="btn"
            disabled={Boolean(busy)}
            onClick={() =>
              run(
                "write",
                () => api.post(`${base}/write`, {}),
                "Capítulo redigido. Revise o texto e as notas de origem.",
              )
            }
          >
            {busy === "write" ? "Redigindo…" : "Redigir com IA"}
          </button>
        </div>
      ) : (
        <>
          <div className="blocks">
            {blocks.map((b, i) => (
              <div key={i} className="block-edit">
                <input
                  value={b.heading ?? ""}
                  placeholder="Título da seção (opcional)"
                  aria-label="Título da seção"
                  disabled={locked}
                  onChange={(e) => upd(i, { heading: e.target.value || undefined })}
                />
                <textarea
                  value={b.text}
                  rows={Math.min(14, Math.max(3, Math.ceil(b.text.length / 90)))}
                  aria-label="Texto do bloco"
                  disabled={locked}
                  onChange={(e) => upd(i, { text: e.target.value })}
                />
                <div className="seg">
                  <span className="segment-meta">Origem:</span>
                  {refs.map((r) => {
                    const on = b.sources.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        className={`chip-toggle${on ? " on" : ""}`}
                        aria-pressed={on}
                        disabled={locked}
                        title={sources.find((s) => s.ref === r)?.title}
                        onClick={() =>
                          upd(i, { sources: on ? b.sources.filter((x) => x !== r) : [...b.sources, r] })
                        }
                      >
                        {r}
                      </button>
                    );
                  })}
                  {!locked && (
                    <button
                      type="button"
                      className="btn ghost small"
                      style={{ marginLeft: "auto" }}
                      onClick={() => setBlocks((x) => x.filter((_, k) => k !== i))}
                    >
                      Remover bloco
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!locked && (
            <div className="row-between">
              <button
                className="btn ghost small"
                onClick={() => setBlocks((x) => [...x, { text: "", sources: [] }])}
              >
                Incluir bloco
              </button>
              {dirty && (
                <div className="seg">
                  <button className="btn ghost small" onClick={() => setBlocks(chapter.blocks)}>
                    Descartar alterações
                  </button>
                  <button
                    className="btn small"
                    disabled={Boolean(busy)}
                    onClick={() => run("save", () => api.put(base, { blocks }), "Alterações salvas.")}
                  >
                    Salvar alterações
                  </button>
                </div>
              )}
            </div>
          )}
          {!locked && (
            <div className="regen">
              <input
                value={instruction}
                maxLength={500}
                placeholder="Orientação para nova versão (ex.: linguagem mais simples, mais exemplos)"
                aria-label="Orientação para a IA"
                onChange={(e) => setInstruction(e.target.value)}
              />
              <button
                className="btn ghost small"
                disabled={Boolean(busy)}
                onClick={() =>
                  run(
                    "write",
                    () => api.post(`${base}/write`, { instruction: instruction || undefined }),
                    "Nova versão redigida. A anterior ficou no histórico.",
                  ).then(() => setInstruction(""))
                }
              >
                {busy === "write" ? "Redigindo…" : "Gerar nova versão"}
              </button>
            </div>
          )}
          {chapter.history.length > 0 && (
            <div>
              <button className="btn ghost small" onClick={() => setShowHistory((v) => !v)}>
                Histórico ({chapter.history.length})
              </button>
              {showHistory && (
                <ul className="history">
                  {chapter.history.map((h, i) => (
                    <li key={h.at + i}>
                      <span>
                        {new Date(h.at).toLocaleString("pt-BR")} · {h.reason}
                      </span>
                      {!locked && (
                        <button
                          className="btn ghost small"
                          onClick={() =>
                            run(
                              "restore",
                              () => api.post(`${base}/restore`, { index: i }),
                              "Versão restaurada.",
                            )
                          }
                        >
                          Restaurar
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ConflictCard({
  conflict,
  locked,
  onSave,
}: {
  conflict: Conflict;
  locked: boolean;
  onSave: (r: string | null) => void;
}) {
  const [text, setText] = useState(conflict.resolution ?? "");
  return (
    <div className={`conflict${conflict.resolution ? " done" : ""}`}>
      <div className="row-between">
        <strong>{conflict.topic}</strong>
        <span className={`badge ${conflict.resolution ? "ok" : "warn"}`}>
          {conflict.resolution ? "Decidida" : "Aguardando decisão"}
        </span>
      </div>
      <p className="segment-meta">{conflict.description}</p>
      <ul>
        {conflict.positions.map((p) => (
          <li key={p.source}>
            <span className="ref small">{p.source}</span> {p.claim}
            {!conflict.resolution && !locked && (
              <button
                className="btn ghost small"
                onClick={() => setText(`Adotar a posição de ${p.source}: ${p.claim}`.slice(0, 600))}
              >
                Adotar
              </button>
            )}
          </li>
        ))}
      </ul>
      {conflict.resolution ? (
        <div className="row-between">
          <p style={{ margin: 0 }}>
            <strong>Decisão:</strong> {conflict.resolution}
          </p>
          {!locked && (
            <button className="btn ghost small" onClick={() => onSave(null)}>
              Rever decisão
            </button>
          )}
        </div>
      ) : (
        !locked && (
          <div className="regen">
            <input
              value={text}
              maxLength={600}
              placeholder="Descreva a decisão (ex.: manter o texto como está)"
              aria-label="Decisão do autor"
              onChange={(e) => setText(e.target.value)}
            />
            <button
              className="btn small"
              disabled={text.trim().length < 3}
              onClick={() => onSave(text.trim())}
            >
              Registrar decisão
            </button>
          </div>
        )
      )}
    </div>
  );
}

// ─────────────────────────── 4. Ebook e curso ───────────────────────────

function EbookTab({ data, reload }: TabProps) {
  const p = data.project;
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [level, setLevel] = useState("beginner");
  const total = data.chapters.length;
  const written = data.chapters.filter((c) => c.blocks.length).length;
  const approved = data.chapters.filter((c) => c.status === "approved").length;
  const open = data.chapters.reduce((n, c) => n + c.openConflicts, 0);
  const canExport = total > 0 && written === total && open === 0;
  const latest = p.exports.at(-1);

  async function exportPdf() {
    setBusy("export");
    setMsg(null);
    try {
      const r = await api.post<{ version: number; pages: number }>(`/authoring/${p.id}/export`);
      await reload();
      setMsg({ kind: "ok", text: `Versão ${r.version} gerada com ${r.pages} páginas.` });
    } catch (err) {
      setMsg({ kind: "error", text: msgOf(err) });
    } finally {
      setBusy("");
    }
  }
  async function toCourse() {
    setBusy("course");
    setMsg(null);
    try {
      const r = await api.post<{ ebookId: string }>(`/authoring/${p.id}/course`, {
        level,
        version: latest?.version,
      });
      navigate(`/professor/ebooks/${r.ebookId}`);
    } catch (err) {
      setMsg({ kind: "error", text: msgOf(err) });
      setBusy("");
    }
  }

  const check = (ok: boolean, text: string) => (
    <li className={ok ? "ok" : ""}>
      <span aria-hidden="true">{ok ? "✓" : "○"}</span> {text}
    </li>
  );
  return (
    <div className="studio-grid">
      <section className="card card-pad stack">
        <h2 className="studio-h">Ebook em PDF</h2>
        <ul className="checklist">
          {check(total > 0, `Sumário com ${total} capítulo(s)`)}
          {check(total > 0 && written === total, `Capítulos redigidos: ${written} de ${total}`)}
          {check(
            open === 0,
            open ? `${open} divergência(s) aguardando decisão` : "Divergências entre fontes decididas",
          )}
          {check(
            total > 0 && approved === total,
            `Capítulos aprovados: ${approved} de ${total} (recomendado)`,
          )}
        </ul>
        <p className="segment-meta">
          O PDF traz capa com a marca da instituição, ficha de créditos, sumário com páginas, os capítulos com
          as marcas de origem [F1], [F2]… e as referências às fontes usadas.
        </p>
        <button className="btn" disabled={!canExport || Boolean(busy)} onClick={() => void exportPdf()}>
          {busy === "export" ? "Gerando…" : latest ? "Gerar nova versão do PDF" : "Gerar ebook em PDF"}
        </button>
        {msg && <p className={`alert ${msg.kind}`}>{msg.text}</p>}
        {p.exports.length > 0 && (
          <ul className="history">
            {[...p.exports].reverse().map((e) => (
              <li key={e.version}>
                <span>
                  Versão {e.version} · {e.pages} páginas · {formatBytes(e.sizeBytes)} ·{" "}
                  {new Date(e.createdAt).toLocaleString("pt-BR")}
                </span>
                <span className="seg">
                  <a
                    className="btn ghost small"
                    href={`/api/authoring/${p.id}/exports/${e.version}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir
                  </a>
                  <a
                    className="btn ghost small"
                    href={`/api/authoring/${p.id}/exports/${e.version}/pdf?download=1`}
                  >
                    Baixar
                  </a>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card card-pad stack bridge">
        <h2 className="studio-h">Gerar curso a partir deste ebook</h2>
        <p className="segment-meta">
          O ebook segue para o mesmo caminho dos ebooks enviados: leitura do PDF, geração de aulas, exercícios
          e avaliação, revisão lado a lado e certificado.
        </p>
        {p.courseId && (
          <p className="alert ok">
            Um curso já foi criado a partir deste projeto.{" "}
            <Link to={`/professor/cursos/${p.courseId}`}>Abrir o curso</Link>
          </p>
        )}
        <div className="field">
          <label htmlFor="b-level">Nível do curso</label>
          <select id="b-level" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="beginner">Iniciante</option>
            <option value="intermediate">Intermediário</option>
            <option value="advanced">Avançado</option>
          </select>
        </div>
        <button className="btn" disabled={!latest || Boolean(busy)} onClick={() => void toCourse()}>
          {busy === "course"
            ? "Criando…"
            : latest
              ? `Gerar curso com a versão ${latest.version}`
              : "Gere o PDF primeiro"}
        </button>
      </section>
    </div>
  );
}
